import { useCallback } from "react";
import { t } from "i18next";
import moment from "moment";
import { useSelector } from "react-redux";

import { SOURCE } from "@constants/Bookings";
import { slotType } from "@enums/hooks.enum";
import { DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { UseBookingSubmitParams } from "@models/components/booking.model";
import { RootState } from "@store/index";

export const useBookingSubmit = ({
  selectedPaymentMethod,
  setGuestError,
  totalPriceWithTax,
}: UseBookingSubmitParams) => {
  const { bookSlot } = useBooking();

  const {
    checkparticipantErrors,
    guestList,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const handleBookingSubmit = useCallback(() => {
    const min = selectedSlot?.slot?.min ?? 0;
    const max = selectedSlot?.slot?.max ?? Infinity;
    const participantCount = guestList.length;
    const allowBringingGuest = selectedSlot?.slot?.allowBringingGuest;

    if (Object.keys(checkparticipantErrors).length > 0) {
      setGuestError(t("addGuest.errorInParticipant"));
      return false;
    }

    if (!allowBringingGuest && participantCount > 0) {
      setGuestError(t("addGuest.guestRestrictionOnPolicy"));
      return false;
    }

    if (allowBringingGuest && participantCount < min) {
      setGuestError(t("addGuest.minGuestRequired", { min }));
      return false;
    }

    if (allowBringingGuest && participantCount > max) {
      setGuestError(t("addGuest.maxGuestsExceeded", { max }));
      return false;
    }

    setGuestError("");

    // Build common payload
    const commonPayload = {
      date: selectedDate,
      participants: guestList.map(p => p.profileUuid),
      paymentMethod: selectedPaymentMethod,
      resourceId: selectedSlot?.resourceId,
      slotType:
        selectedSlot?.slot?.type === slotType.TimeSlots
          ? selectedSlot?.slot?.type
          : selectedSlot?.slot?.slot,
      source: globalThis.matchMedia("(display-mode: standalone)").matches
        ? SOURCE.PWA
        : SOURCE.WEB,
      timeSlot: selectedSlot?.slot?.slot,
      totalPrice: totalPriceWithTax,
    };

    const endTime = moment(selectedTime, DateFormats.Time)
      .add(selectedSlot?.slot?.slot, DateFormats.Minutes)
      .format(DateFormats.Time);

    if (selectedSlot?.slot?.type === slotType.TimeSlots) {
      bookSlot({
        ...commonPayload,
        endTime: endTime,
        startTime: selectedTime,
      });
    } else {
      bookSlot({
        ...commonPayload,
        endTime: selectedSlot?.slot?.ends,
        startTime: selectedSlot?.slot?.starts,
      });
    }

    return true;
  }, [
    selectedSlot,
    guestList,
    checkparticipantErrors,
    selectedDate,
    selectedTime,
    selectedPaymentMethod,
    totalPriceWithTax,
    setGuestError,
    bookSlot,
  ]);

  return { handleBookingSubmit };
};
